import React from "react";
import { TailSpin } from "react-loader-spinner";

import type { SurveyStats, SurveySummaryPayload } from "../../lib/api";
import QuestionResultCard from "./QuestionResultCard";
import { formatCount, formatDuration, formatPercent } from "./surveyTypes";

type Props = {
  summary: SurveySummaryPayload | undefined;
  isPending: boolean;
  isError: boolean;
  errorMessage: string;
  onRetry: () => void;
};

const FUNNEL: {
  key: keyof SurveyStats;
  label: string;
  format: (v: SurveyStats[keyof SurveyStats]) => string;
  hint?: string;
}[] = [
  { key: "views", label: "CTA views", format: (v) => formatCount(v as number) },
  { key: "starts", label: "Started", format: (v) => formatCount(v as number) },
  {
    key: "completions",
    label: "Completed",
    format: (v) => formatCount(v as number),
  },
  {
    key: "completionRate",
    label: "Completion rate",
    format: (v) => formatPercent(v as number),
  },
  {
    key: "avgDurationSeconds",
    label: "Median time",
    format: (v) => formatDuration(v as number | null),
  },
];

const SurveySummaryTab: React.FC<Props> = ({
  summary,
  isPending,
  isError,
  errorMessage,
  onRetry,
}) => {
  if (isPending) {
    return (
      <div className="flex justify-center rounded-2xl border border-[#2C363E] bg-[#161E25]/40 py-20">
        <TailSpin color="#0B75C2" height={36} width={36} />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col gap-2 rounded-xl border border-red-500/35 bg-red-500/[0.08] px-4 py-3 text-[13px] text-red-100/95 sm:flex-row sm:items-center sm:justify-between">
        <span>{errorMessage}</span>
        <button
          type="button"
          onClick={onRetry}
          className="shrink-0 rounded-lg border border-red-500/40 bg-red-500/20 px-3 py-1.5 text-[12px] font-semibold text-red-200"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!summary || summary.questions.length === 0) {
    return (
      <p className="rounded-2xl border border-dashed border-[#2C363E] px-4 py-16 text-center text-[13px] text-[#5C6B76]">
        This survey has no questions yet.
      </p>
    );
  }

  return (
    <div className="space-y-5">
      <section
        className="grid grid-cols-2 gap-3 rounded-2xl border border-[#2C363E] bg-[#161E25]/40 p-4 sm:grid-cols-3 lg:grid-cols-5"
        aria-label="Response funnel"
      >
        {FUNNEL.map((item) => (
          <div key={String(item.key)}>
            <p className="font-sora text-[20px] font-bold tabular-nums text-white">
              {item.format(summary.stats[item.key])}
            </p>
            <p className="mt-1 text-[11px] font-medium text-[#869AA9]">
              {item.label}
            </p>
          </div>
        ))}
      </section>

      <p className="text-[12px] text-[#5C6B76]">
        Results below cover completed responses only. Abandoned attempts are counted
        in the funnel above and listed under Responses.
      </p>

      {summary.questions.map((q, i) => (
        <QuestionResultCard key={q.questionKey} index={i} summary={q} />
      ))}
    </div>
  );
};

export default SurveySummaryTab;
